import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { User, X, Swords } from 'lucide-react';
import { type ChallengeData } from '../services/matchmaking';
import { joinMatch } from '../services/matchmaking';
import { cn } from '../lib/utils';

interface IncomingChallengeModalProps {
  challenge: ChallengeData;
  myUid: string;
  myName: string;
  onAccept: (matchCode: string) => void;
  onDecline: () => void;
}

export default function IncomingChallengeModal({
  challenge,
  myUid,
  myName,
  onAccept,
  onDecline,
}: IncomingChallengeModalProps) {
  const [joining, setJoining] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleAccept = async () => {
    setJoining(true);
    setError(null);
    try {
      const result = await joinMatch(challenge.matchCode, myUid, myName);
      if (result.success) {
        onAccept(challenge.matchCode);
      } else {
        setError('Impossibile unirsi alla partita.');
        setJoining(false);
      }
    } catch (err) {
      setError('Errore durante la connessione.');
      setJoining(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1e] rounded-2xl w-full max-w-sm overflow-hidden"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        {/* Header */}
        <div className="flex items-center justify-center gap-3 p-6 border-b border-white/10">
          <Swords className="w-6 h-6 text-yellow-400" />
          <h2 className="text-lg font-bold text-white">Sfida in arrivo!</h2>
          <Swords className="w-6 h-6 text-yellow-400" />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-2xl font-bold">
 🎴
            </div>
            <div>
              <div className="text-white font-bold text-lg">{challenge.fromName}</div>
              <div className="text-white/40 text-sm">ti ha sfidato a Sbarazzo</div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onDecline}
              disabled={joining}
              className="flex-1 px-4 py-3 bg-white/10 hover:bg-red-500/50 text-white/70 hover:text-white text-sm font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Rifiuta
            </button>
            <button
              onClick={handleAccept}
              disabled={joining}
              className={cn(
                'flex-1 px-4 py-3 text-white text-sm font-bold rounded-xl transition-colors flex items-center justify-center gap-2',
                joining
                  ? 'bg-blue-600/50 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-500'
              )}
            >
              <User className="w-4 h-4" />
              {joining ? 'Connessione...' : 'Accetta'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
